import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Heart,
  Weight,
  Thermometer,
  Activity,
  Ruler,
  Circle,
  Calculator,
  Wind,
  Gauge,
  Droplets,
  ChevronLeft,
  MoreHorizontal,
  CalendarIcon,
  AlertCircle,
  AlertTriangle,
} from "lucide-react";
import {
  useVitalsFormState,
  handleSubmit,
  vitalTypes,
  additionalVitalTypes,
  allVitalTypes,
  weightUnits,
  temperatureUnits,
  heightUnits,
  circumferenceUnits,
  bloodSugarUnits,
  getVitalTypeLabel,
  getVitalTypeIcon,
  VitalType,
  useBMIHeightPrefill,
  useHeightUnitSwitch,
  evaluateBmiPlausibility,
  evaluateWeightPlausibility,
} from "@/lib/vitalsData";
import { usePatientVitalsState } from "@/lib/dashboard/vitalsData";
import { ethnicityTypes } from "@/enum/ethnicityType";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface VitalsModalProps {
  trigger: React.ReactNode;
  vitalType: VitalType;
  onSave: (vital: any) => void;
  isDarkMode?: boolean;
}

export function VitalsModal({
  trigger,
  vitalType,
  onSave,
  isDarkMode = false,
}: VitalsModalProps) {
  const {
    open,
    setOpen,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    clearErrors,
    getDefaultValues,
  } = useVitalsFormState(vitalType);
  const { patientVitals, selectedPatientDetails } = usePatientVitalsState();

  const onSubmit = (data: any) => {
    if (bmiBlocksSave || weightBlocksSave) return;
    const ethnicity = watch("ethnicity") || "";
    const completeData = {
      ...data,
      ethnicity: ethnicity,
    };

    if (handleSubmit) {
      handleSubmit(completeData, vitalType, onSave, setOpen, reset);
    } else {
      onSave(completeData);
      setOpen(false);
      reset();
    }
  };

  const renderIcon = () => {
    const iconName = getVitalTypeIcon(vitalType);
    switch (iconName) {
      case "Heart":
        return <Heart className="w-5 h-5" />;
      case "Weight":
        return <Weight className="w-5 h-5" />;
      case "Activity":
        return <Activity className="w-5 h-5" />;
      case "Thermometer":
        return <Thermometer className="w-5 h-5" />;
      case "Wind":
        return <Wind className="w-5 h-5" />;
      case "Gauge":
        return <Gauge className="w-5 h-5" />;
      case "AlertCircle":
        return <AlertCircle className="w-5 h-5" />;
      case "Droplets":
        return <Droplets className="w-5 h-5" />;
      case "Ruler":
        return <Ruler className="w-5 h-5" />;
      case "Circle":
        return <Circle className="w-5 h-5" />;
      case "Calculator":
        return <Calculator className="w-5 h-5" />;
      case "Brain":
        return <Activity className="w-5 h-5" />;
      default:
        return <Heart className="w-5 h-5" />;
    }
  };

  React.useEffect(() => {
    if (vitalType === "bmi" && open) {
      const existingBmi = patientVitals?.Bmi || patientVitals?.bmi;

      if (existingBmi?.ethnicity || existingBmi?.Ethnicity) {
        const ethnicityValue = String(
          existingBmi.ethnicity || existingBmi.Ethnicity || "",
        );

        // Check if the value exists in ethnicityTypes enum
        if (ethnicityValue && ethnicityTypes.hasOwnProperty(ethnicityValue)) {
          setValue("ethnicity", ethnicityValue);
        }
      }
    }
  }, [vitalType, open, patientVitals, setValue]);

  const [
    shouldPrepopulateEthnicity,
    setShouldPrepopulateEthnicity,
  ] = React.useState(false);
  const [initialEthnicityValue, setInitialEthnicityValue] = React.useState("");

  React.useEffect(() => {
    if (open && vitalType === "bmi") {
      const patientEthnicity = selectedPatientDetails?.ethnicity;

      if (patientEthnicity !== undefined && patientEthnicity !== null) {
        let ethnicityValue = String(patientEthnicity);

        if (ethnicityTypes.hasOwnProperty(ethnicityValue)) {
          setInitialEthnicityValue(ethnicityValue);
          setShouldPrepopulateEthnicity(true);
        } else {
          const foundEntry = Object.entries(ethnicityTypes).find(
            ([key, value]) => {
              return key === ethnicityValue || String(key) === ethnicityValue;
            },
          );

          if (foundEntry) {
            setInitialEthnicityValue(foundEntry[0]);
            setShouldPrepopulateEthnicity(true);
          }
        }
      }
    }

    return () => {
      setShouldPrepopulateEthnicity(false);
      setInitialEthnicityValue("");
    };
  }, [open, vitalType, selectedPatientDetails]);

  React.useEffect(() => {
    if (
      open &&
      vitalType === "bmi" &&
      shouldPrepopulateEthnicity &&
      initialEthnicityValue
    ) {
      setValue("ethnicity", initialEthnicityValue, { shouldValidate: true });

      setShouldPrepopulateEthnicity(false);
    }
  }, [
    open,
    vitalType,
    shouldPrepopulateEthnicity,
    initialEthnicityValue,
    setValue,
    patientVitals,
  ]);

  // --- BMI plausibility ---
  const calculateBmiRaw = (): number | null => {
    const heightUnit = watch("heightUnit");
    const weightUnit = watch("weightUnit");
    const height = watch("height");
    const feet = watch("feet");
    const inch = watch("inch");
    const weight = watch("weight");

    let heightInMeters = 0;
    let weightInKg = 0;

    if (heightUnit === "cm") {
      if (!height) return null;
      heightInMeters = parseFloat(height) / 100;
    } else if (heightUnit === "ft/in") {
      if (!feet && !inch) return null;
      heightInMeters =
        (parseFloat(feet) || 0) * 0.3048 + (parseFloat(inch) || 0) * 0.0254;
    }

    if (!weight) return null;
    weightInKg =
      weightUnit === "lbs" ? parseFloat(weight) * 0.453592 : parseFloat(weight);

    if (heightInMeters > 0 && weightInKg > 0) {
      return weightInKg / (heightInMeters * heightInMeters);
    }
    return null;
  };

  const bmiRawValue = vitalType === "bmi" ? calculateBmiRaw() : null;
  const bmiPlausibility =
    bmiRawValue !== null ? evaluateBmiPlausibility(bmiRawValue) : null;

  const bmiInputSignature = [
    watch("height"),
    watch("heightUnit"),
    watch("feet"),
    watch("inch"),
    watch("weight"),
    watch("weightUnit"),
  ].join("|");

  const [bmiConfirmed, setBmiConfirmed] = React.useState(false);
  const bmiConfirmedForSignature = React.useRef<string>("");

  React.useEffect(() => {
    if (bmiConfirmedForSignature.current !== bmiInputSignature)
      setBmiConfirmed(false);
  }, [bmiInputSignature]);

  const handleConfirmBmiPlausibility = () => {
    bmiConfirmedForSignature.current = bmiInputSignature;
    setBmiConfirmed(true);
  };

  const bmiBlocksSave =
    vitalType === "bmi" &&
    bmiPlausibility !== null &&
    (bmiPlausibility.tier === "hard_reject" ||
      (bmiPlausibility.tier === "confirmation_prompt" && !bmiConfirmed));

  // --- Weight plausibility ---
  const weightRawValue =
    vitalType === "weight" && watch("value")
      ? parseFloat(watch("value"))
      : null;
  const weightPlausibility =
    weightRawValue !== null && !isNaN(weightRawValue)
      ? evaluateWeightPlausibility(
          weightRawValue,
          watch("unit") as "kg" | "lbs",
        )
      : null;

  const weightInputSignature = [watch("value"), watch("unit")].join("|");

  const [weightConfirmed, setWeightConfirmed] = React.useState(false);
  const weightConfirmedForSignature = React.useRef<string>("");

  React.useEffect(() => {
    if (weightConfirmedForSignature.current !== weightInputSignature)
      setWeightConfirmed(false);
  }, [weightInputSignature]);

  const handleConfirmWeightPlausibility = () => {
    weightConfirmedForSignature.current = weightInputSignature;
    setWeightConfirmed(true);
  };

  const weightBlocksSave =
    vitalType === "weight" &&
    weightPlausibility !== null &&
    (weightPlausibility.tier === "hard_reject" ||
      (weightPlausibility.tier === "confirmation_prompt" && !weightConfirmed));

  //   if (lastHeight.unit === "ft/in") {
  //     setValue("heightUnit", "ft/in");
  //     setValue("feet", lastHeight.feet ?? "");
  //     setValue("inch", lastHeight.inches ?? "");
  //   }
  // }, [open, vitalType, patientVitals?.height, reset, setValue]);

  const renderFormFields = () => {
    switch (vitalType) {
      case "heart-rate":
        return (
          <div className="space-y-2">
            <Label
              htmlFor="value"
              className="text-sm font-medium text-gray-700"
            >
              Heart Rate (BPM) *
            </Label>
            <Input
              id="value"
              type="number"
              placeholder="e.g., 72"
              {...register("value")}
              className="w-full"
            />
            {errors.value && (
              <p className="text-sm text-red-600">{errors.value.message}</p>
            )}
          </div>
        );

      case "weight":
        return (
          <>
            <div className="space-y-2">
              <Label
                htmlFor="value"
                className="text-sm font-medium text-gray-700"
              >
                Weight *
              </Label>
              <Input
                id="value"
                type="number"
                step="0.1"
                placeholder="e.g., 70.5"
                {...register("value")}
                className="w-full"
              />
              {errors.value && (
                <p className="text-sm text-red-600">{errors.value.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Unit *
              </Label>
              <Select
                value={watch("unit")}
                // onValueChange={(value) => {
                //   setValue("unit", value);

                //   console.log(value);
                // }}

                onValueChange={(newUnit) => {
                  const currentWeight = watch("value");
                  const currentUnit = watch("unit");

                  if (
                    currentWeight &&
                    !isNaN(parseFloat(currentWeight)) &&
                    currentUnit !== newUnit
                  ) {
                    const weightNum = parseFloat(currentWeight);
                    let convertedWeight: number;

                    if (currentUnit === "kg" && newUnit === "lbs") {
                      convertedWeight = parseFloat(
                        (weightNum * 2.2).toFixed(1),
                      );
                    } else if (currentUnit === "lbs" && newUnit === "kg") {
                      convertedWeight = parseFloat(
                        (weightNum / 2.2).toFixed(1),
                      );
                    } else {
                      convertedWeight = weightNum;
                    }

                    setValue("value", convertedWeight.toString());
                  }

                  setValue("unit", newUnit);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {weightUnits.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {weightPlausibility?.tier === "hard_reject" && (
                <Alert className="text-red-900 border-red-200 bg-red-50">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <AlertDescription>
                    {weightPlausibility.message}
                  </AlertDescription>
                </Alert>
              )}

              {weightPlausibility?.tier === "confirmation_prompt" &&
                !weightConfirmed && (
                  <div className="p-3 border rounded-lg border-amber-200 bg-amber-50">
                    <div className="flex items-start gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                      <p className="text-sm text-amber-900">
                        {weightPlausibility.message}
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleConfirmWeightPlausibility}
                      className="border-amber-400 text-amber-900 hover:bg-amber-100"
                    >
                      Confirm value is correct
                    </Button>
                  </div>
                )}

              {weightPlausibility?.tier === "confirmation_prompt" &&
                weightConfirmed && (
                  <div className="text-xs px-3 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800">
                    Unusual value confirmed
                  </div>
                )}
            </div>
          </>
        );

      case "blood-pressure":
        return (
          <>
            <div className="space-y-2">
              <Label
                htmlFor="systolic"
                className="text-sm font-medium text-gray-700"
              >
                Systolic Pressure (mmHg) *
              </Label>
              <Input
                id="systolic"
                type="number"
                placeholder="e.g., 120"
                {...register("systolic")}
                className="w-full"
              />
              {errors.systolic && (
                <p className="text-sm text-red-600">
                  {errors.systolic.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="diastolic"
                className="text-sm font-medium text-gray-700"
              >
                Diastolic Pressure (mmHg) *
              </Label>
              <Input
                id="diastolic"
                type="number"
                placeholder="e.g., 80"
                {...register("diastolic")}
                className="w-full"
              />
              {errors.diastolic && (
                <p className="text-sm text-red-600">
                  {errors.diastolic.message}
                </p>
              )}
            </div>
          </>
        );

      case "temperature":
        return (
          <>
            <div className="space-y-2">
              <Label
                htmlFor="value"
                className="text-sm font-medium text-gray-700"
              >
                Temperature *
              </Label>
              <Input
                id="value"
                type="number"
                step="0.1"
                placeholder="e.g., 36.5"
                {...register("value")}
                className="w-full"
              />
              {errors.value && (
                <p className="text-sm text-red-600">{errors.value.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Unit *
              </Label>
              <Select
                value={watch("unit")}
                onValueChange={(value) => setValue("unit", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {temperatureUnits.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case "respiratory-rate":
        return (
          <div className="space-y-2">
            <Label
              htmlFor="value"
              className="text-sm font-medium text-gray-700"
            >
              Respiratory Rate (breaths/min) *
            </Label>
            <Input
              id="value"
              type="number"
              placeholder="e.g., 16"
              {...register("value")}
              className="w-full"
            />
            {errors.value && (
              <p className="text-sm text-red-600">{errors.value.message}</p>
            )}
          </div>
        );

      case "oxygen-saturation":
        return (
          <div className="space-y-2">
            <Label
              htmlFor="value"
              className="text-sm font-medium text-gray-700"
            >
              Oxygen Saturation (%) *
            </Label>
            <Input
              id="value"
              type="number"
              placeholder="e.g., 98"
              min="0"
              max="100"
              {...register("value")}
              className="w-full"
            />
            {errors.value && (
              <p className="text-sm text-red-600">{errors.value.message}</p>
            )}
          </div>
        );

      case "news-pain-score":
        return (
          <div className="space-y-2">
            <Label
              htmlFor="value"
              className="text-sm font-medium text-gray-700"
            >
              NEWS Pain Score (0-10) *
            </Label>
            <Input
              id="value"
              type="number"
              placeholder="e.g., 5"
              min="0"
              max="10"
              {...register("value")}
              className="w-full"
            />
            {errors.value && (
              <p className="text-sm text-red-600">{errors.value.message}</p>
            )}
            <p className="text-xs text-gray-500">
              0 = No pain, 10 = Worst possible pain
            </p>
          </div>
        );

      case "blood-sugar":
        return (
          <>
            <div className="space-y-2">
              <Label
                htmlFor="value"
                className="text-sm font-medium text-gray-700"
              >
                Blood Sugar *
              </Label>
              <Input
                id="value"
                type="number"
                step="0.1"
                placeholder="e.g., 120"
                {...register("value")}
                className="w-full"
              />
              {errors.value && (
                <p className="text-sm text-red-600">{errors.value.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Unit *
              </Label>
              <Select
                value={watch("unit")}
                onValueChange={(value) => setValue("unit", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {bloodSugarUnits.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        );

      case "height":
        return (
          <>
            {/* CM height field */}
            {watch("unit") === "cm" && (
              <div className="space-y-2">
                <Label
                  htmlFor="value"
                  className="text-sm font-medium text-gray-700"
                >
                  Height (cm) *
                </Label>
                <Input
                  id="value"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 170"
                  {...register("value")}
                  className="w-full"
                />
                {errors.value && (
                  <p className="text-sm text-red-600">{errors.value.message}</p>
                )}
              </div>
            )}

            {/* Unit selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Unit *
              </Label>
              <Select
                value={watch("unit")}
                onValueChange={(value) => setValue("unit", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {heightUnits.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* FT / IN fields */}
            {watch("unit") === "ft/in" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label
                    htmlFor="feet"
                    className="text-sm font-medium text-gray-700"
                  >
                    Feet
                  </Label>
                  <Input
                    id="feet"
                    type="number"
                    placeholder="e.g., 5"
                    {...register("feet")}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="inches"
                    className="text-sm font-medium text-gray-700"
                  >
                    Inches
                  </Label>
                  <Input
                    id="inches"
                    type="number"
                    placeholder="e.g., 8"
                    {...register("inches")}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </>
        );

      case "circumference":
        return (
          <>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Unit *
              </Label>
              <Select
                value={watch("unit")}
                onValueChange={(value) => setValue("unit", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {circumferenceUnits.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label
                  htmlFor="waist"
                  className="text-sm font-medium text-gray-700"
                >
                  Waist Circumference
                </Label>
                <Input
                  id="waist"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 80"
                  {...register("waist")}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="hip"
                  className="text-sm font-medium text-gray-700"
                >
                  Hip Circumference
                </Label>
                <Input
                  id="hip"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 90"
                  {...register("hip")}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="neck"
                  className="text-sm font-medium text-gray-700"
                >
                  Neck Circumference
                </Label>
                <Input
                  id="neck"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 35"
                  {...register("neck")}
                  className="w-full"
                />
              </div>
            </div>
          </>
        );

      case "bmi":
        const calculateBMI = () => {
          const heightUnit = watch("heightUnit");
          const weightUnit = watch("weightUnit");
          const height = watch("height");
          const feet = watch("feet");
          const inch = watch("inch");
          const weight = watch("weight");
          let heightInMeters = 0;
          let weightInKg = 0;

          if (heightUnit === "cm") {
            heightInMeters = parseFloat(height) / 100;
          } else if (heightUnit === "ft/in") {
            const feetNum = parseFloat(feet) || 0;
            const inchNum = parseFloat(inch) || 0;
            heightInMeters = feetNum * 0.3048 + inchNum * 0.0254;
          }

          if (weightUnit === "kg") {
            weightInKg = parseFloat(weight);
          } else if (weightUnit === "lbs") {
            weightInKg = parseFloat(weight) * 0.453592;
          }

          if (heightInMeters && weightInKg && heightInMeters > 0)
            return (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
          return "";
        };

        const bmi = calculateBMI();

        // Use the ethnicityTypes enum to create options
        const ethnicityOptions = Object.entries(ethnicityTypes).map(
          ([key, label]) => ({
            value: key,
            label: label,
          }),
        );

        const getBMIThresholds = (ethnicity?: string) => {
          const whiteThresholds = {
            underweight: 18.5,
            normal: 25,
            overweight: 30,
          };

          const nonWhiteThresholds = {
            underweight: 18.5,
            normal: 23,
            overweight: 27.5,
          };

          // If no ethnicity selected, use standard WHO thresholds
          if (!ethnicity || ethnicity === "" || ethnicity === "0") {
            return whiteThresholds;
          }

          // Get the ethnicity key and label
          const ethnicityKey = String(ethnicity);
          const ethnicityLabel =
            ethnicityTypes[ethnicityKey as keyof typeof ethnicityTypes] || "";

          // Check if it's a White ethnicity
          // This checks both the key and the label
          const isWhiteEthnicity =
            ethnicityKey.toLowerCase().includes("white") ||
            ethnicityLabel.toLowerCase().includes("white");

          if (isWhiteEthnicity) {
            return whiteThresholds;
          }

          // For all other ethnicities, use non-White thresholds
          return nonWhiteThresholds;
        };

        const getBMICategory = (bmi: number, ethnicity?: string): string => {
          const thresholds = getBMIThresholds(ethnicity);

          if (bmi < thresholds.underweight) return "Underweight";
          if (bmi < thresholds.normal) return "Normal weight";
          if (bmi < thresholds.overweight) return "Overweight";
          return "Obese";
        };

        const getBMIColorClass = (category: string) => {
          switch (category) {
            case "Normal weight":
              return "bg-green-50 text-green-800 border-green-300";
            case "Underweight":
              return "bg-yellow-50 text-yellow-800 border-yellow-300";
            case "Overweight":
              return "bg-amber-50 text-amber-800 border-amber-300";
            case "Obese":
              return "bg-red-50 text-red-800 border-red-300";
            default:
              return "bg-gray-50 text-gray-800 border-gray-200";
          }
        };
        const ethnicity = watch("ethnicity");
        const bmiCategory = bmi
          ? getBMICategory(parseFloat(bmi), ethnicity)
          : "";
        const bmiColorClass = getBMIColorClass(bmiCategory);

        const ethnicityLabel =
          ethnicity && ethnicity !== ""
            ? ethnicityTypes[ethnicity as keyof typeof ethnicityTypes]
            : "";

        return (
          <>
            {/* Height */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                {watch("heightUnit") === "ft/in" ? (
                  <>
                    <Label className="text-sm font-medium text-gray-700">
                      Height *
                    </Label>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Input
                          id="feet"
                          type="number"
                          placeholder="Feet"
                          {...register("feet")}
                        />
                        {errors.feet && (
                          <p className="text-sm text-red-600">
                            {errors.feet.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Input
                          id="inch"
                          type="number"
                          placeholder="Inch"
                          {...register("inch")}
                        />
                        {errors.inch && (
                          <p className="text-sm text-red-600">
                            {errors.inch.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <Label
                      htmlFor="height"
                      className="text-sm font-medium text-gray-700"
                    >
                      Height *
                    </Label>

                    <Input
                      id="height"
                      type="number"
                      step="0.1"
                      placeholder="e.g., 170"
                      {...register("height")}
                    />

                    {errors.height && (
                      <p className="text-sm text-red-600">
                        {errors.height.message}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Height Unit *
                </Label>

                <Select
                  value={watch("heightUnit")}
                  onValueChange={(val) => {
                    setValue("heightUnit", val);
                    setValue("height", "");
                    setValue("feet", "");
                    setValue("inch", "");
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {heightUnits.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Height Section */}
            {/* <div className="grid grid-cols-2 gap-3">
              {watch("heightUnit") === "ft/in" ? (
                <>
                  <div className="space-y-2">
                    <Label
                      htmlFor="feet"
                      className="text-sm font-medium text-gray-700"
                    >
                      Feet *
                    </Label>
                    <Input
                      id="feet"
                      type="number"
                      placeholder="e.g., 5"
                      {...register("feet")}
                      className="w-full"
                    />
                    {errors.feet && (
                      <p className="text-sm text-red-600">
                        {errors.feet.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="inch"
                      className="text-sm font-medium text-gray-700"
                    >
                      Inch
                    </Label>
                    <Input
                      id="inch"
                      type="number"
                      placeholder="e.g., 6"
                      {...register("inch")}
                      className="w-full"
                    />
                    {errors.inch && (
                      <p className="text-sm text-red-600">
                        {errors.inch.message}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="col-span-2 space-y-2">
                  <Label
                    htmlFor="height"
                    className="text-sm font-medium text-gray-700"
                  >
                    Height (cm) *
                  </Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 170"
                    {...register("height")}
                    className="w-full"
                  />
                  {errors.height && (
                    <p className="text-sm text-red-600">
                      {errors.height.message}
                    </p>
                  )}
                </div>
              )}
            </div> */}

            {/* Height Unit */}
            {/* <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Height Unit *
              </Label>
              <Select
                value={watch("heightUnit")}
                onValueChange={(val) => {
                  setValue("heightUnit", val);
                  setValue("height", "");
                  setValue("feet", "");
                  setValue("inch", "");
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {heightUnits.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div> */}

            {/* Weight Section */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label
                  htmlFor="weight"
                  className="text-sm font-medium text-gray-700"
                >
                  Weight *
                </Label>
                <Input
                  id="weight"
                  type="number"
                  step="0.1"
                  placeholder="e.g., 70"
                  {...register("weight")}
                  className="w-full"
                />
                {errors.weight && (
                  <p className="text-sm text-red-600">
                    {errors.weight.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">
                  Weight Unit *
                </Label>
                <Select
                  value={watch("weightUnit")}
                  onValueChange={(newUnit) => {
                    const currentWeight = watch("weight");
                    const currentUnit = watch("weightUnit");

                    if (
                      currentWeight &&
                      !isNaN(parseFloat(currentWeight)) &&
                      currentUnit !== newUnit
                    ) {
                      const weightNum = parseFloat(currentWeight);
                      let convertedWeight: number;

                      if (currentUnit === "kg" && newUnit === "lbs") {
                        convertedWeight = parseFloat(
                          (weightNum * 2.2).toFixed(1),
                        );
                      } else if (currentUnit === "lbs" && newUnit === "kg") {
                        convertedWeight = parseFloat(
                          (weightNum / 2.2).toFixed(1),
                        );
                      } else {
                        convertedWeight = weightNum;
                      }

                      setValue("weight", convertedWeight.toString());
                    }

                    setValue("weightUnit", newUnit);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {weightUnits.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Ethnicity Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Ethnicity (Optional)
              </Label>
              <Select
                value={ethnicity || ""}
                onValueChange={(value) => setValue("ethnicity", value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select ethnicity">
                    {ethnicity && ethnicity !== ""
                      ? ethnicityTypes[ethnicity as keyof typeof ethnicityTypes]
                      : "Select ethnicity"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Select ethnicity (optional)</SelectItem>
                  {ethnicityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <input
                type="hidden"
                {...register("ethnicity")}
                value={ethnicity || ""}
              />
              <p className="text-xs text-gray-500">
                Ethnicity is collected for demographic purposes only. BMI is
                calculated using standard thresholds.
              </p>
            </div>

            {/* BMI Display */}
            {bmi && (
              <div
                className={`border rounded-xl p-4 flex items-center justify-between ${bmiColorClass}`}
              >
                <div className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  <div className="flex flex-col">
                    <span className="font-medium">BMI: {bmi}</span>
                    {ethnicity &&
                      ethnicity !== "" &&
                      ethnicity !== "0" &&
                      ethnicityLabel && (
                        <></>
                        // <span className="text-xs opacity-75">
                        //   Ethnicity: {ethnicityLabel.split(" - ")[0]}
                        // </span>
                      )}
                  </div>
                </div>
                <span className="text-sm font-semibold">{bmiCategory}</span>
              </div>
            )}
            {bmiPlausibility?.tier === "hard_reject" && (
              <Alert className="text-red-900 border-red-200 bg-red-50">
                <AlertCircle className="w-4 h-4 text-red-600" />
                <AlertDescription>{bmiPlausibility.message}</AlertDescription>
              </Alert>
            )}

            {bmiPlausibility?.tier === "confirmation_prompt" && !bmiConfirmed && (
              <div className="p-3 border rounded-lg border-amber-200 bg-amber-50">
                <div className="flex items-start gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
                  <p className="text-sm text-amber-900">
                    {bmiPlausibility.message}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleConfirmBmiPlausibility}
                  className="border-amber-400 text-amber-900 hover:bg-amber-100"
                >
                  Confirm value is correct
                </Button>
              </div>
            )}

            {bmiPlausibility?.tier === "confirmation_prompt" &&
              bmiConfirmed && (
                <div className="text-xs px-3 py-1.5 rounded-md border border-amber-200 bg-amber-50 text-amber-800">
                  Unusual value confirmed
                </div>
              )}
          </>
        );
      case "consciousness-level":
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Consciousness Level (AVPU) *
            </Label>
            <Select
              value={watch("value") || "Alert"}
              onValueChange={(value) => setValue("value", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select consciousness level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Alert">Alert</SelectItem>
                <SelectItem value="Confusion">New Confusion (C)</SelectItem>
                <SelectItem value="Voice">Voice (V)</SelectItem>
                <SelectItem value="Pain">Pain (P)</SelectItem>
                <SelectItem value="Unresponsive">Unresponsive (U)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              AVPU scale — Alert is the only score that contributes 0 to NEWS2.
              All others score 3.
            </p>
            {errors.value && (
              <p className="text-sm text-red-600">{errors.value.message}</p>
            )}
          </div>
        );

      case "supplemental-oxygen":
        return (
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">
              Is the patient on supplemental oxygen? *
            </Label>
            <Select
              value={watch("value") || "No"}
              onValueChange={(value) => setValue("value", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Yes">Yes</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              If yes, SpO₂ will use NEWS2 Scale 2 and adds +2 to score.
            </p>
            {errors.value && (
              <p className="text-sm text-red-600">{errors.value.message}</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const unit = watch("unit");
  useBMIHeightPrefill(vitalType, open, patientVitals, setValue, reset);
  useHeightUnitSwitch(unit, setValue, clearErrors);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            {renderIcon()}
            Add {getVitalTypeLabel(vitalType)}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-6">
          {renderFormFields()}

          {/* Date Field */}
          <div
            className={`space-y-2 border-t pt-4 transition-colors duration-300 ${
              isDarkMode ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <Label
              htmlFor="date"
              className={`text-sm font-medium flex items-center gap-1 transition-colors duration-300 ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Date Taken
            </Label>
            <Input
              id="date"
              type="date"
              {...register("date")}
              className={`w-full transition-colors duration-300 ${
                isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""
              }`}
              max={new Date().toISOString().split("T")[0]}
            />
            <p
              className={`text-xs transition-colors duration-300 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              You can backdate when this vital was taken
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              disabled={bmiBlocksSave || weightBlocksSave}
              type="submit"
              className="flex-1 bg-slate-900 hover:bg-slate-800"
            >
              Add {getVitalTypeLabel(vitalType)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddVitalsButton({
  vitalType,
  onSave,
  trigger,
}: {
  vitalType: VitalType;
  onSave: (vital: any) => void;
  trigger?: React.ReactNode;
}) {
  return (
    <VitalsModal
      trigger={
        trigger || (
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-0 font-normal text-gray-500 hover:text-gray-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add {getVitalTypeLabel(vitalType)}
          </Button>
        )
      }
      vitalType={vitalType}
      onSave={onSave}
    />
  );
}

export function VitalTypeSelectorModal({
  onSave,
  trigger,
  isDarkMode = false,
}: {
  onSave: (vital: any) => void;
  trigger?: React.ReactNode;
  isDarkMode?: boolean;
}) {
  const [selectorOpen, setSelectorOpen] = React.useState(false);
  const [
    selectedVitalType,
    setSelectedVitalType,
  ] = React.useState<VitalType | null>(null);
  const [showAdditionalVitals, setShowAdditionalVitals] = React.useState(false);

  const handleVitalTypeSelect = (vitalType: VitalType) => {
    setSelectedVitalType(vitalType);
    setSelectorOpen(false);
    // Small delay to ensure selector modal is closed before opening vitals modal
    setTimeout(() => {
      const vitalButton = document.querySelector(
        `[data-vital-type="${vitalType}"]`,
      ) as HTMLButtonElement;
      if (vitalButton) {
        vitalButton.click();
      }
    }, 100);
  };

  const handleVitalSave = (vital: any) => {
    onSave(vital);
    setSelectedVitalType(null);
  };

  return (
    <>
      <Dialog open={selectorOpen} onOpenChange={setSelectorOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button
              variant="ghost"
              size="sm"
              className={`p-0 h-auto font-normal transition-colors duration-300 ${
                isDarkMode
                  ? "text-gray-400 hover:text-white"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Vital
            </Button>
          )}
        </DialogTrigger>
        <DialogContent
          className={`sm:max-w-md transition-colors duration-300 ${
            isDarkMode
              ? "bg-gray-800 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <DialogHeader>
            <DialogTitle
              className={`flex items-center gap-2 transition-colors duration-300 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {showAdditionalVitals && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdditionalVitals(false)}
                  className={`p-1 h-auto ${
                    isDarkMode
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
              )}
              {showAdditionalVitals ? "Additional Vitals" : "Select Vital Type"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {(showAdditionalVitals ? additionalVitalTypes : vitalTypes).map(
              (vitalType) => {
                const IconComponent =
                  vitalType.icon === "Heart"
                    ? Heart
                    : vitalType.icon === "Weight"
                    ? Weight
                    : vitalType.icon === "Activity"
                    ? Activity
                    : vitalType.icon === "Thermometer"
                    ? Thermometer
                    : vitalType.icon === "Wind"
                    ? Wind
                    : vitalType.icon === "Gauge"
                    ? Gauge
                    : vitalType.icon === "AlertCircle"
                    ? AlertCircle
                    : vitalType.icon === "Droplets"
                    ? Droplets
                    : vitalType.icon === "Ruler"
                    ? Ruler
                    : vitalType.icon === "Circle"
                    ? Circle
                    : vitalType.icon === "Calculator"
                    ? Calculator
                    : Thermometer;

                return (
                  <Button
                    key={vitalType.value}
                    variant="outline"
                    onClick={() =>
                      handleVitalTypeSelect(vitalType.value as VitalType)
                    }
                    className={`flex flex-col items-center gap-2 h-20 transition-colors duration-300 ${
                      isDarkMode
                        ? "border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white"
                        : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900"
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="text-sm">{vitalType.label}</span>
                  </Button>
                );
              },
            )}

            {/* Other button - only show on main screen */}
            {!showAdditionalVitals && (
              <Button
                variant="outline"
                onClick={() => setShowAdditionalVitals(true)}
                className={`flex flex-col items-center gap-2 h-20 transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-200 hover:text-white"
                    : "border-gray-200 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900"
                }`}
              >
                <MoreHorizontal className="w-5 h-5" />
                <span className="text-sm">Other</span>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Hidden VitalsModal triggers for each vital type */}
      {/* {vitalTypes.map((vitalType) => (
        <VitalsModal
          key={vitalType.value}
          trigger={
            <button
              data-vital-type={vitalType.value}
              style={{ display: "none" }}
            />
          }
          vitalType={vitalType.value as VitalType}
          onSave={handleVitalSave}
          isDarkMode={isDarkMode}
        />
      ))} */}
      {allVitalTypes.map((vitalType) => (
        <VitalsModal
          key={vitalType.value}
          trigger={
            <button
              data-vital-type={vitalType.value}
              style={{ display: "none" }}
            />
          }
          vitalType={vitalType.value as VitalType}
          onSave={handleVitalSave}
          isDarkMode={isDarkMode}
        />
      ))}
    </>
  );
}
